import { useState, useEffect, useCallback } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface Order {
  id: string;
  type: 'buy' | 'sell';
  makerId: number;
  makerUsername?: string;
  crypto: string;
  network: string;
  fiat: string;
  amount: number;
  price: number;
  totalFiat: number;
  paymentMethods: string[];
  status: 'open' | 'matched' | 'escrow' | 'completed' | 'cancelled' | 'disputed';
  createdAt: string;
  updatedAt: string;
}

export function useOrders(userId?: number) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/orders?userId=${userId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }
      const data = await response.json();
      setOrders(data.orders || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const cancelOrder = useCallback(async (orderId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/orders/${orderId}/cancel`, {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error('Failed to cancel order');
      }
      // Refresh orders
      await fetchOrders();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    }
  }, [fetchOrders]);

  return {
    orders,
    loading,
    error,
    refetch: fetchOrders,
    cancelOrder,
  };
}

export function useOrderbook(crypto?: string, fiat?: string) {
  const [orders, setOrders] = useState<{ buy: Order[]; sell: Order[] }>({ buy: [], sell: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrderbook = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      let url = `${API_URL}/api/orderbook`;
      const params = new URLSearchParams();
      if (crypto) params.append('crypto', crypto);
      if (fiat) params.append('fiat', fiat);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch orderbook');
      }
      const data = await response.json();
      setOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [crypto, fiat]);

  useEffect(() => {
    fetchOrderbook();
    
    // Poll every 10 seconds
    const interval = setInterval(fetchOrderbook, 10000);
    return () => clearInterval(interval);
  }, [fetchOrderbook]);

  return {
    buyOrders: orders.buy,
    sellOrders: orders.sell,
    loading,
    error,
    refetch: fetchOrderbook,
  };
}
